import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fondosAPI, cajasAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { ExportActions } from '../components/ExportActions';
import { exportListado, imprimirListadoColumnas } from '../utils/exportListado';
import { useBranding } from '../context/BrandingContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { loadFiltrosSession, saveFiltrosSession } from '../utils/filtrosSession';
import '../Clientes.css';

const formatMoney = (n) =>
  `$${(parseFloat(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CLAVE_FILTROS = 'fondos_listado_filtros';
const defaultFiltrosFondos = { tipo: '', nombre: '' };

const COLUMNAS_EXPORT = [
  { id: 'tipo', label: 'Tipo' },
  { id: 'nombre', label: 'Nombre' },
  { id: 'banco', label: 'Banco / Número' },
  { id: 'moneda', label: 'Moneda' },
  { id: 'saldo', label: 'Saldo' },
  { id: 'estado', label: 'Estado' },
];

const COLUMNAS_EXPORT_IDS = COLUMNAS_EXPORT.map((c) => c.id);

const valorCeldaFondoExport = (e, colId) => {
  switch (colId) {
    case 'tipo':
      return e.tipo_entidad === 'caja' ? 'Caja' : 'Cuenta';
    case 'nombre':
      return e.nombre || '';
    case 'banco':
      return e.tipo_entidad === 'cuenta'
        ? `${e.banco_nombre || ''} ${e.numero_cuenta || ''}`.trim()
        : '';
    case 'moneda':
      return e.moneda || 'ARS';
    case 'saldo':
      return formatMoney(parseFloat(e.saldo) || 0);
    case 'estado':
      return e.activo ? 'Activo' : 'Inactivo';
    default:
      return '';
  }
};

const FondosListado = () => {
  const { hasPermission, esAdmin } = useAuth();
  const { logoUrl } = useBranding();
  const { formatFecha, fechaHoy } = useZonaHoraria();
  const [entidades, setEntidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refrescando, setRefrescando] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState(() => loadFiltrosSession(CLAVE_FILTROS, defaultFiltrosFondos).tipo || '');
  const [filtroNombre, setFiltroNombre] = useState(() => loadFiltrosSession(CLAVE_FILTROS, defaultFiltrosFondos).nombre || '');
  const primeraCargaRef = useRef(true);
  const cargaSeqRef = useRef(0);
  const [ajusteCaja, setAjusteCaja] = useState(null);
  const [ajusteSaldoNuevo, setAjusteSaldoNuevo] = useState('');
  const [ajusteMotivo, setAjusteMotivo] = useState('');
  const [ajustando, setAjustando] = useState(false);

  useEffect(() => {
    saveFiltrosSession(CLAVE_FILTROS, { tipo: filtroTipo, nombre: filtroNombre });
  }, [filtroTipo, filtroNombre]);

  const loadEntidades = useCallback(async () => {
    const mostrarLoaderPantalla = primeraCargaRef.current;
    const seq = ++cargaSeqRef.current;
    try {
      if (mostrarLoaderPantalla) setLoading(true);
      else setRefrescando(true);
      const params = { activo: true };
      if (filtroTipo) params.tipo = filtroTipo;
      const res = await fondosAPI.getEntidades(params);
      if (seq !== cargaSeqRef.current) return;
      if (res.success) {
        setEntidades(Array.isArray(res.data) ? res.data : []);
      } else if (mostrarLoaderPantalla) {
        setEntidades([]);
      }
    } catch (e) {
      if (seq !== cargaSeqRef.current) return;
      toast.error(e.message || 'Error al cargar fondos');
      if (mostrarLoaderPantalla) setEntidades([]);
    } finally {
      if (mostrarLoaderPantalla) {
        setLoading(false);
        primeraCargaRef.current = false;
      }
      if (seq === cargaSeqRef.current) setRefrescando(false);
    }
  }, [filtroTipo]);

  useEffect(() => {
    loadEntidades();
  }, [loadEntidades]);

  const filtradas = useMemo(() => {
    const q = filtroNombre.trim().toLowerCase();
    if (!q) return entidades;
    return entidades.filter(
      (e) =>
        (e.nombre || '').toLowerCase().includes(q) ||
        (e.banco_nombre || '').toLowerCase().includes(q) ||
        (e.numero_cuenta || '').toLowerCase().includes(q)
    );
  }, [entidades, filtroNombre]);

  const rutaMovimientos = (e) =>
    e.tipo_entidad === 'caja'
      ? `/movimientos-fondos?caja_id=${e.id}`
      : `/movimientos-fondos?cuenta_id=${e.id}`;

  const rutaEditar = (e) =>
    e.tipo_entidad === 'caja' ? `/cajas/editar/${e.id}` : `/cuentas-bancarias/editar/${e.id}`;

  const stateMovimiento = (e, clase) => {
    const base = { modoFondos: true, clase_movimiento: clase };
    if (e.tipo_entidad === 'caja') {
      if (clase === 'deposito') return { ...base, caja_origen_id: e.id };
      if (clase === 'extraccion') return { ...base, caja_destino_id: e.id };
      if (clase === 'transferencia_caja') return { ...base, caja_origen_id: e.id };
    } else {
      if (clase === 'deposito') return { ...base, cuenta_bancaria_destino_id: e.id };
      if (clase === 'extraccion') return { ...base, cuenta_bancaria_origen_id: e.id };
      if (clase === 'transferencia_cuenta') return { ...base, cuenta_bancaria_origen_id: e.id };
    }
    return base;
  };

  const handleExportListado = async (format) => {
    if (!filtradas.length) {
      toast.info('No hay fondos para exportar');
      return;
    }
    const headers = ['Tipo', 'Nombre', 'Banco / Número', 'Moneda', 'Saldo', 'Estado'];
    const rows = filtradas.map((e) => [
      e.tipo_entidad === 'caja' ? 'Caja' : 'Cuenta',
      e.nombre || '',
      e.tipo_entidad === 'cuenta' ? `${e.banco_nombre || ''} ${e.numero_cuenta || ''}`.trim() : '',
      e.moneda || 'ARS',
      (parseFloat(e.saldo) || 0).toFixed(2),
      e.activo ? 'Activo' : 'Inactivo',
    ]);
    try {
      await exportListado({ filename: 'lista_fondos', sheetName: 'Fondos', headers, rows, format });
    } catch (e) {
      toast.error(e.message || 'Error al exportar');
    }
  };

  const generarPDFListado = () => {
    if (!filtradas.length) {
      toast.info('No hay fondos para exportar');
      return;
    }
    const partes = [`Generado el ${formatFecha(fechaHoy())}`];
    if (filtroTipo === 'caja') partes.push('Tipo: Cajas');
    else if (filtroTipo === 'cuenta') partes.push('Tipo: Cuentas bancarias');
    if (filtroNombre.trim()) partes.push(`Búsqueda: ${filtroNombre.trim()}`);
    try {
      imprimirListadoColumnas({
        titulo: 'Lista de fondos',
        subtitulo: partes.join(' · '),
        columnDefs: COLUMNAS_EXPORT,
        visibleColumnIds: COLUMNAS_EXPORT_IDS,
        items: filtradas,
        getCellValue: valorCeldaFondoExport,
        logoUrl,
      });
    } catch (e) {
      toast.error(e.message || 'Error al imprimir');
    }
  };

  const abrirAjusteCaja = (entidad) => {
    setAjusteCaja(entidad);
    setAjusteSaldoNuevo(String(parseFloat(entidad.saldo) || 0));
    setAjusteMotivo('');
  };

  const cerrarAjusteCaja = () => {
    if (ajustando) return;
    setAjusteCaja(null);
    setAjusteSaldoNuevo('');
    setAjusteMotivo('');
  };

  const confirmarAjusteCaja = async () => {
    if (!ajusteCaja) return;
    const saldoNum = parseFloat(String(ajusteSaldoNuevo).replace(',', '.'));
    if (!Number.isFinite(saldoNum)) {
      toast.warning('Ingrese un saldo válido');
      return;
    }
    try {
      setAjustando(true);
      const res = await cajasAPI.ajusteSaldo(ajusteCaja.id, {
        saldo_nuevo: saldoNum,
        motivo: ajusteMotivo.trim() || undefined,
      });
      if (!res.success) {
        toast.error(res.message || 'No se pudo ajustar');
        return;
      }
      toast.success(res.message || 'Saldo ajustado');
      setAjusteCaja(null);
      setAjusteSaldoNuevo('');
      setAjusteMotivo('');
      await loadEntidades();
    } catch (e) {
      toast.error(e.message || 'Error al ajustar saldo');
    } finally {
      setAjustando(false);
    }
  };

  return (
    <div className="clientes pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Lista de fondos</h1>
        <p className="page-subtitle">Cajas y cuentas bancarias con saldo calculado</p>
        {!loading && !refrescando && filtradas.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <ExportActions
              onCsv={() => handleExportListado('csv')}
              onExcel={() => handleExportListado('excel')}
              onPrint={generarPDFListado}
              printLabel="PDF"
            />
          </div>
        )}
      </div>

      <div className="listado-panel clientes-listado listado-controles">
      <div className="filters-section" style={{ marginBottom: 0 }}>
        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="filtro-tipo-fondo">Tipo</label>
            <select
              id="filtro-tipo-fondo"
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="caja">Cajas</option>
              <option value="cuenta">Cuentas bancarias</option>
            </select>
            {refrescando && (
              <span className="filters-refresh-hint" style={{ display: 'block', marginTop: 4 }}>
                Actualizando…
              </span>
            )}
          </div>
          <div className="filter-group">
            <label htmlFor="filtro-nombre-fondo">Buscar</label>
            <input
              id="filtro-nombre-fondo"
              type="text"
              value={filtroNombre}
              onChange={(e) => setFiltroNombre(e.target.value)}
              placeholder="Nombre, banco o número de cuenta"
            />
          </div>
        </div>
      </div>

      <div className="listado-panel-cuerpo clientes-listado-cuerpo">
      <div
        className={
          refrescando
            ? 'listado-tabla-area clientes-listado-tabla-area listado-tabla-area--refreshing clientes-listado-tabla-area--refreshing'
            : 'listado-tabla-area clientes-listado-tabla-area'
        }
      >
        {refrescando && (
          <div className="clientes-listado-refresh-overlay" aria-live="polite">
            <div className="clientes-listado-refresh-spinner" />
            <span className="clientes-listado-refresh-label">Actualizando…</span>
          </div>
        )}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Nombre</th>
              <th>Banco / Número</th>
              <th>Moneda</th>
              <th>Saldo</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && filtradas.length === 0 ? (
              <tr>
                <td colSpan={7} className="no-data">Cargando…</td>
              </tr>
            ) : filtradas.length === 0 ? (
              <tr>
                <td colSpan={7} className="no-data">No hay fondos que coincidan</td>
              </tr>
            ) : (
              filtradas.map((e) => {
                const saldo = parseFloat(e.saldo) || 0;
                return (
                  <tr key={`${e.tipo_entidad}-${e.id}`}>
                    <td>
                      <span className="badge activo">{e.tipo_entidad === 'caja' ? 'Caja' : 'Cuenta'}</span>
                    </td>
                    <td><strong>{e.nombre}</strong></td>
                    <td>
                      {e.tipo_entidad === 'cuenta'
                        ? `${e.banco_nombre || ''} ${e.numero_cuenta || ''}`.trim()
                        : '—'}
                    </td>
                    <td>{e.moneda || 'ARS'}</td>
                    <td style={{ fontWeight: 600 }}>{formatMoney(saldo)}</td>
                    <td>
                      <span className={`badge ${e.activo ? 'activo' : 'inactivo'}`}>
                        {e.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Link to={rutaMovimientos(e)} className="btn-action-icon" title="Ver movimientos">
                          <span aria-hidden="true">📋</span>
                        </Link>
                        {hasPermission('crear_movimientos_fondos') && e.tipo_entidad === 'caja' && (
                          <Link
                            to="/movimientos/nuevo"
                            state={stateMovimiento(e, 'deposito')}
                            className="btn-action-icon"
                            title="Depósito (caja → cuenta)"
                          >
                            <span aria-hidden="true">➕</span>
                          </Link>
                        )}
                        {hasPermission('crear_movimientos_fondos') && e.tipo_entidad === 'cuenta' && (
                          <Link
                            to="/movimientos/nuevo"
                            state={stateMovimiento(e, 'extraccion')}
                            className="btn-action-icon"
                            title="Extracción (cuenta → caja)"
                          >
                            <span aria-hidden="true">➖</span>
                          </Link>
                        )}
                        {hasPermission('crear_movimientos_fondos') && (
                          <Link
                            to="/movimientos/nuevo"
                            state={stateMovimiento(
                              e,
                              e.tipo_entidad === 'caja' ? 'transferencia_caja' : 'transferencia_cuenta'
                            )}
                            className="btn-action-icon"
                            title="Transferencia entre fondos"
                          >
                            <span aria-hidden="true">↔</span>
                          </Link>
                        )}
                        {hasPermission('ver_movimientos') && e.tipo_entidad === 'caja' && (
                          <Link
                            to={`/cartera-cheques?caja_id=${e.id}&estado=recibido`}
                            className="btn-action-icon"
                            title="Cheques en cartera en esta caja"
                          >
                            <span aria-hidden="true">🧾</span>
                          </Link>
                        )}
                        {esAdmin && e.tipo_entidad === 'caja' && (
                          <button
                            type="button"
                            className="btn-action-icon"
                            title="Ajuste de saldo (sin movimiento)"
                            onClick={() => abrirAjusteCaja(e)}
                          >
                            <span aria-hidden="true">⚖</span>
                          </button>
                        )}
                        {hasPermission('cerrar_caja') && e.tipo_entidad === 'caja' && (
                          <Link
                            to={`/cierre-caja?caja_id=${e.id}`}
                            className="btn-action-icon"
                            title="Cerrar caja"
                          >
                            <span aria-hidden="true">🔒</span>
                          </Link>
                        )}
                        {((e.tipo_entidad === 'caja' && hasPermission('ver_movimientos')) ||
                          (e.tipo_entidad === 'cuenta' && hasPermission('ver_cuentas_bancarias'))) && (
                          <Link to={rutaEditar(e)} className="btn-action-icon btn-action-edit" title="Editar">
                            <span aria-hidden="true">✏️</span>
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      </div>
      </div>
      </div>

      <p style={{ marginTop: 12, fontSize: '0.9rem', color: '#666', flexShrink: 0 }}>
        <Link to="/movimientos-fondos">Ver todos los movimientos de fondos</Link>
      </p>

      {ajusteCaja && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ajuste-caja-titulo"
          onClick={cerrarAjusteCaja}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: '#fff',
              padding: 24,
              borderRadius: 8,
              maxWidth: 420,
              width: '100%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="ajuste-caja-titulo" style={{ marginTop: 0 }}>Ajuste de caja</h2>
            <p style={{ color: '#64748b', fontSize: 14, marginTop: 0 }}>
              Ajusta el saldo calculado de <strong>{ajusteCaja.nombre}</strong> al día de hoy
              ({formatFecha(fechaHoy())}) <em>sin generar movimientos</em>. Solo administrador.
            </p>
            <div className="form-group">
              <label>Saldo actual</label>
              <input type="text" value={formatMoney(ajusteCaja.saldo)} disabled readOnly />
            </div>
            <div className="form-group">
              <label htmlFor="ajuste-saldo-nuevo">Saldo nuevo</label>
              <input
                id="ajuste-saldo-nuevo"
                type="number"
                step="0.01"
                value={ajusteSaldoNuevo}
                onChange={(e) => setAjusteSaldoNuevo(e.target.value)}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="ajuste-motivo">Motivo (opcional)</label>
              <input
                id="ajuste-motivo"
                type="text"
                maxLength={200}
                value={ajusteMotivo}
                onChange={(e) => setAjusteMotivo(e.target.value)}
                placeholder="Ej. arqueo / corrección de apertura"
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="btn-secondary" onClick={cerrarAjusteCaja} disabled={ajustando}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={confirmarAjusteCaja} disabled={ajustando}>
                {ajustando ? 'Guardando…' : 'Confirmar ajuste'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FondosListado;
